import type { CreatorRow } from "../api/creators";

export type ExportColumn = {
  header: string;
  accessor: (row: CreatorRow) => string | number | null;
};

export const EXPORT_COLUMNS: ExportColumn[] = [
  { header: "created_at", accessor: (r) => r.created_at },
  { header: "name", accessor: (r) => r.name },
  { header: "email", accessor: (r) => r.email },
  { header: "type", accessor: (r) => r.creator_type },
  { header: "status", accessor: (r) => r.status },
  { header: "origin", accessor: (r) => r.origin },
  { header: "persona", accessor: (r) => r.tagline },
  { header: "description", accessor: (r) => r.description },
  { header: "link", accessor: (r) => r.link },
  { header: "work_count", accessor: (r) => r.work_count },
  // "true"/"false" rather than the UI's ✓/blank: a blank cell is
  // indistinguishable from missing data in a spreadsheet, and these values
  // match the API field and sort/filter cleanly in Excel.
  { header: "verified", accessor: (r) => (r.verified ? "true" : "false") },
];

function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildCsv(rows: CreatorRow[]): Blob {
  const headerLine = EXPORT_COLUMNS.map((c) => csvField(c.header)).join(",");
  const lines = rows.map((r) =>
    EXPORT_COLUMNS.map((c) => csvField(c.accessor(r))).join(","),
  );
  const content = [headerLine, ...lines].join("\r\n") + "\r\n";
  return new Blob([content], { type: "text/csv;charset=utf-8" });
}

export async function buildXlsx(rows: CreatorRow[]): Promise<Blob> {
  // write-excel-file, not SheetJS: `xlsx` is frozen at 0.18.5 on npm (SheetJS
  // moved distribution to their own CDN) and carries two unpatchable high
  // advisories. Both are in the READER, which this app never used — but a
  // dependency that can never be patched is not one to keep. (86bbjv18u)
  //
  // The /browser entry is named explicitly: this package has no bare "." export,
  // and pinning the entry keeps test and bundle resolution identical.
  const writeXlsxFile = (await import("write-excel-file/browser")).default;
  const data = [
    EXPORT_COLUMNS.map((c) => ({ value: c.header, type: String })),
    ...rows.map((r) =>
      EXPORT_COLUMNS.map((c) => {
        const v = c.accessor(r);
        // A bare null is a valid empty cell; { value: null } is not — the Cell
        // union allows null itself but CellObject.value does not.
        if (v === null || v === undefined) return null;
        // Keep numbers numeric so they sort and aggregate in Excel rather than
        // landing as left-aligned text.
        return typeof v === "number"
          ? { value: v, type: Number }
          : { value: String(v), type: String };
      }),
    ),
  ];
  // No fileName argument, so the writer hands back { toBlob, toFile } rather
  // than triggering a download itself — this app owns the download step.
  const out = await (await writeXlsxFile(data, { sheet: "Creators" })).toBlob();
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportFilename(format: "csv" | "xlsx"): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `creators-${stamp}.${format}`;
}
